/* eslint no-use-before-define: ["error", { "variables": false }] */

import { DEFAULT_PLACEHOLDER, MIN_COMPOSER_HEIGHT, COMPOSER_INPUT_CONFIG } from './Constant';

import Color from './Color';
import PropTypes from 'prop-types';
import React from 'react';

export default class Composer extends React.Component {
  constructor(props) {
    super(props);
    this.mouseDownTime = null;
    this.isMouseDown = false;
    this.isStartingRef = false;
    this.isStoppingRef = false;
    this.textareaRef = React.createRef();
    this.voiceButtonRef = React.createRef();
    this.containerRef = React.createRef();
    this.eventListenersBound = {
      mouseup: null,
      touchend: null,
      touchcancel: null
    };
    this.updateHeightTimer = null;
    this.state = {
      isHovered: false,
      inputHeight: COMPOSER_INPUT_CONFIG.MIN_HEIGHT
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isRecording && !this.props.isRecording) {
      this.resetRecordingState();
    }
    if (!prevProps.isRecording && this.props.isRecording) {
      this.isStartingRef = false;
    }
    if (prevProps.text !== this.props.text && !this.props.isVoiceMode) {
      this.updateInputHeight();
    }
  }

  componentDidMount() {
    if (!this.props.isVoiceMode) {
      this.updateInputHeight();
    }
    this.setupGlobalEventListeners();
  }

  componentWillUnmount() {
    this.cleanupGlobalEventListeners();
    if (this.updateHeightTimer) {
      clearTimeout(this.updateHeightTimer);
      this.updateHeightTimer = null;
    }
    this.resetRecordingState();
  }

  resetRecordingState() {
    this.isMouseDown = false;
    this.mouseDownTime = null;
    this.isStartingRef = false;
    this.isStoppingRef = false;
  }

  setupGlobalEventListeners() {
    this.cleanupGlobalEventListeners();

    const handlers = {
      mouseup: this.handleGlobalMouseUp.bind(this),
      touchend: this.handleGlobalTouchEnd.bind(this),
      touchcancel: this.handleGlobalTouchCancel.bind(this)
    };

    const options = { passive: true };
    Object.entries(handlers).forEach(([key, handler]) => {
      this.eventListenersBound[key] = handler;
      document.addEventListener(key, handler, options);
    });
  }

  cleanupGlobalEventListeners() {
    Object.keys(this.eventListenersBound).forEach((key) => {
      const handler = this.eventListenersBound[key];
      if (handler) {
        try {
          document.removeEventListener(key, handler);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`Error removing ${key} listener:`, error);
          }
        }
        this.eventListenersBound[key] = null;
      }
    });
  }

  updateInputHeight() {
    if (!this.textareaRef.current || this.props.isVoiceMode) {
      return;
    }

    const textarea = this.textareaRef.current;
    const { MIN_HEIGHT, MAX_HEIGHT } = COMPOSER_INPUT_CONFIG;
    const scrollTop = textarea.scrollTop;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.max(MIN_HEIGHT, Math.min(scrollHeight, MAX_HEIGHT));

    textarea.style.height = `${newHeight}px`;
    if (this.containerRef.current) {
      this.containerRef.current.style.height = `${newHeight}px`;
    }

    this.setState({ inputHeight: newHeight });
    this.props.onInputSizeChanged({ height: newHeight });

    textarea.scrollTop = scrollHeight > MAX_HEIGHT ? scrollTop : 0;

    try {
      textarea.setSelectionRange(selectionStart, selectionEnd);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting selection range:', e);
      }
    }
  }

  onContentSizeChange(e) {
    const { contentSize } = e.nativeEvent;
    if (!contentSize) {
      return;
    }

    if (
      !this.contentSize ||
      this.contentSize.width !== contentSize.width ||
      this.contentSize.height !== contentSize.height
    ) {
      this.contentSize = contentSize;
      this.props.onInputSizeChanged(this.contentSize);
    }
  }

  onChangeText(event) {
    const target = event.target || event;
    this.props.onTextChanged(target);

    if (this.updateHeightTimer) {
      clearTimeout(this.updateHeightTimer);
    }
    this.updateHeightTimer = setTimeout(() => {
      this.updateInputHeight();
      this.updateHeightTimer = null;
    }, 0);
  }

  onKeyDown = (e) => {
    const { text, onSend, isVoiceMode } = this.props;

    if (isVoiceMode || e.keyCode !== 13 || e.shiftKey) {
      return false;
    }

    const trimmedText = (text || '').trim();
    if (trimmedText && onSend) {
      onSend({ text: trimmedText }, true);
      e.preventDefault();
    }
    return false;
  };

  handleStartRecording(callback) {
    if (!callback || this.props.isRecording || this.isStartingRef) {
      return false;
    }

    this.isStartingRef = true;
    this.mouseDownTime = Date.now();
    this.isMouseDown = true;

    try {
      callback();
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error starting recording:', error);
      }
      this.resetRecordingState();
      return false;
    }
  }

  handleStopRecording(callback, requireMouseDown = false) {
    const { isRecording } = this.props;
    if (!callback || !isRecording || this.isStoppingRef) {
      return false;
    }

    if (requireMouseDown && !this.isMouseDown) {
      return false;
    }

    this.isStoppingRef = true;
    this.isMouseDown = false;
    this.mouseDownTime = null;

    try {
      callback();
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error stopping recording:', error);
      }
      this.isStoppingRef = false;
      return false;
    }
  }

  handleMouseDown = (e) => {
    const { isVoiceMode, onStartRecording } = this.props;

    if (!isVoiceMode) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!onStartRecording) {
      this.resetRecordingState();
      return;
    }

    this.handleStartRecording(onStartRecording);
  };

  handleMouseUp = (e) => {
    const { isVoiceMode, onStopRecording } = this.props;
    if (!isVoiceMode) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    this.handleStopRecording(onStopRecording);
  };

  handleGlobalMouseUp = () => {
    const { isVoiceMode, onStopRecording } = this.props;
    if (!isVoiceMode) {
      return;
    }
    this.handleStopRecording(onStopRecording, true);
  };

  handleTouchStart = (e) => {
    const { isVoiceMode, onStartRecording } = this.props;
    if (!isVoiceMode) {
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();

    if (!onStartRecording) {
      this.resetRecordingState();
      return;
    }

    this.handleStartRecording(onStartRecording);
  };

  handleTouchEnd = (e) => {
    const { isVoiceMode, onStopRecording } = this.props;
    if (!isVoiceMode) {
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();
    this.handleStopRecording(onStopRecording);
  };

  handleTouchCancel = (e) => {
    const { isVoiceMode, onStopRecording } = this.props;
    if (!isVoiceMode) {
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();
    this.handleStopRecording(onStopRecording);
  };

  handleGlobalTouchEnd = () => {
    const { isVoiceMode, onStopRecording } = this.props;
    if (!isVoiceMode) {
      return;
    }
    this.handleStopRecording(onStopRecording, true);
  };

  handleGlobalTouchCancel = () => {
    const { isVoiceMode, onStopRecording } = this.props;
    if (!isVoiceMode) {
      return;
    }
    this.handleStopRecording(onStopRecording, true);
  };

  render() {
    const { isVoiceMode, isRecording, placeholder, text, onPaste, textInputStyle, textInputProps } = this.props;
    const { isHovered, inputHeight } = this.state;
    const { LINE_HEIGHT, MIN_HEIGHT, MAX_HEIGHT } = COMPOSER_INPUT_CONFIG;

    if (isVoiceMode) {
      return this.renderVoiceMode(placeholder, text, isRecording, isHovered, MIN_HEIGHT);
    }

    return this.renderTextMode(
      inputHeight,
      MIN_HEIGHT,
      MAX_HEIGHT,
      LINE_HEIGHT,
      placeholder,
      text,
      onPaste,
      textInputStyle,
      textInputProps
    );
  }

  renderVoiceMode(placeholder, text, isRecording, isHovered, minHeight) {
    const isHoveredAndNotRecording = isHovered && !isRecording;
    const buttonHeight = `${minHeight}px`;

    const buttonStyle = {
      marginLeft: 0,
      marginBottom: 0,
      width: '100%',
      height: buttonHeight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      cursor: isRecording ? 'default' : 'pointer',
      borderRadius: '8px',
      backgroundColor: isRecording || isHoveredAndNotRecording ? Color.voiceActive : Color.voiceIdle,
      border: isRecording ? `0.8px solid ${Color.text}` : `1px solid ${Color.text}`,
      color: Color.text,
      fontFamily: 'inherit',
      fontSize: '16px',
      lineHeight: '20px',
      padding: '6px 14px',
      boxSizing: 'border-box',
      outline: 'none',
      userSelect: 'none',
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
      boxShadow: isRecording ? '0 2px 8px rgba(0, 0, 0, 0.15)' : '0 2px 4px rgba(0,0,0,0.1)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    };

    const containerStyle = {
      backgroundColor: Color.inputBg,
      width: '100%',
      height: buttonHeight,
      marginLeft: 12,
      marginRight: 4,
      marginBottom: 0,
      display: 'flex',
      alignItems: 'flex-start',
      boxSizing: 'border-box',
      minWidth: 0,
      flex: 1
    };

    return (
      <div style={containerStyle}>
        <button
          ref={this.voiceButtonRef}
          type='button'
          role='button'
          aria-label={placeholder || '语音输入'}
          style={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            ...buttonStyle
          }}
          onMouseDown={this.handleMouseDown}
          onMouseUp={this.handleMouseUp}
          onMouseEnter={() => !isRecording && this.setState({ isHovered: true })}
          onMouseLeave={() => {
            this.setState({ isHovered: false });
            if (isRecording) {
              this.handleMouseUp({ preventDefault: () => {}, stopPropagation: () => {} });
            }
          }}
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
          onTouchCancel={this.handleTouchCancel}
        >
          {text || placeholder}
        </button>
      </div>
    );
  }

  renderTextMode(
    inputHeight,
    minHeight,
    maxHeight,
    lineHeight,
    placeholder,
    text,
    onPaste,
    textInputStyle,
    textInputProps
  ) {
    const containerStyle = {
      backgroundColor: Color.inputBg,
      width: '100%',
      height: `${inputHeight}px`,
      minHeight: `${minHeight}px`,
      maxHeight: `${maxHeight}px`,
      marginLeft: 15,
      marginBottom: 0,
      display: 'flex',
      alignItems: 'flex-start',
      boxSizing: 'border-box'
    };

    const computedTextInputStyle = {
      ...styles.textInput,
      ...textInputStyle,
      lineHeight: `${lineHeight}px`,
      resize: 'none',
      minHeight: `${minHeight}px`,
      maxHeight: `${maxHeight}px`,
      overflowY: 'auto',
      height: `${inputHeight}px`,
      marginLeft: 0,
      width: '100%',
      backgroundColor: Color.inputBg,
      color: Color.text,
      border: `1px solid ${Color.inputBorder}`
    };

    const { ref: externalRef, ...restTextInputProps } = textInputProps || {};
    const composedRef = (el) => {
      this.textareaRef.current = el;
      if (externalRef) {
        typeof externalRef === 'function' ? externalRef(el) : (externalRef.current = el);
      }
    };

    return (
      <div ref={this.containerRef} style={containerStyle}>
        <textarea
          ref={composedRef}
          onPaste={onPaste}
          onKeyDown={this.onKeyDown}
          placeholder={placeholder}
          onChange={(event) => this.onChangeText(event.target)}
          style={computedTextInputStyle}
          value={text}
          {...restTextInputProps}
        />
      </div>
    );
  }
}

const styles = {
  textInput: {
    marginLeft: 15,
    marginBottom: 0,
    fontSize: 16,
    lineHeight: '20px',
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: 8,
    paddingRight: 8,
    outline: 'none',
    border: `1px solid ${Color.inputBorder}`,
    borderRadius: 8,
    backgroundColor: Color.inputBg,
    color: Color.text,
    overflowY: 'auto',
    overflowX: 'hidden',
    boxShadow: 'none',
    resize: 'none',
    width: '100%',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    boxSizing: 'border-box'
  }
};

Composer.defaultProps = {
  composerHeight: MIN_COMPOSER_HEIGHT,
  text: '',
  placeholderTextColor: Color.defaultProps,
  placeholder: DEFAULT_PLACEHOLDER,
  textInputProps: null,
  multiline: true,
  textInputStyle: {},
  textInputAutoFocus: false,
  keyboardAppearance: 'default',
  onTextChanged: () => {},
  onInputSizeChanged: () => {},
  onPaste: () => {},
  isVoiceMode: false,
  isRecording: false,
  onStartRecording: null,
  onStopRecording: null
};

Composer.propTypes = {
  composerHeight: PropTypes.number,
  text: PropTypes.string,
  placeholder: PropTypes.string,
  placeholderTextColor: PropTypes.string,
  textInputProps: PropTypes.object,
  textInputStyle: PropTypes.object,
  onTextChanged: PropTypes.func,
  onInputSizeChanged: PropTypes.func,
  onPaste: PropTypes.func,
  multiline: PropTypes.bool,
  textInputAutoFocus: PropTypes.bool,
  keyboardAppearance: PropTypes.string,
  isVoiceMode: PropTypes.bool,
  isRecording: PropTypes.bool,
  onStartRecording: PropTypes.func,
  onStopRecording: PropTypes.func
};
